import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomGraphLegend } from '../../../../SharedComponents/CustomGraphLegend';
import { StackedBarShape } from '../../../../SharedComponents/StackedBarShape';
import { CustomGraphTooltip } from '../../../../SharedComponents/CustomGraphTooltip';
import { GraphWrapper } from '../../../../SharedComponents/GraphWrapper';
import { QuarterCustomTick } from '../../../../SharedComponents/QuarterCustomTick';
import { useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { formatNumber, formatStackedNumbers } from '../../../../../utils';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { formatLocalDate } from '../../../../../utils/localDateToDate';
import type { VacantUnitsChartData } from '../MarketDataReport.data';
import { getGraphTitle } from '../../../../../utils/getGraphTitle';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitMarketDataVacantMarketDataEvent } from '../../../../../analytics/events';
import { VacantUnitsFilter } from '../MarketDataReport.hooks';

type VacantUnitsGraphProps = {
  selectedRegionId: number;
  chartData: Array<VacantUnitsChartData>;
  isLoading: boolean;
  buttonGroupFilter: VacantUnitsFilter;
  setButtonGroupFilter: (value: VacantUnitsFilter) => void;
  hasMoodysData: boolean;
};

export const VacantUnitsGraph = ({
  selectedRegionId,
  chartData,
  isLoading,
  buttonGroupFilter,
  setButtonGroupFilter,
  hasMoodysData,
}: VacantUnitsGraphProps) => {
  const { user } = useRentalManagerUser();
  const handleButtonGroupFilterChange = useCallback(
    (value: VacantUnitsFilter) => {
      if (user) emitMarketDataVacantMarketDataEvent({ user });
      setButtonGroupFilter(value);
    },
    [user, setButtonGroupFilter],
  );
  const { filteredProperties, regionsMapping, selectedFilters, isFetchingInitialData, analyticsLastUpdatedDates } =
    useMainLayout();
  const lastUpdatedPortfolioDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioPerformanceLastUpdated);
  const lastUpdatedMarketDate = formatLocalDate(analyticsLastUpdatedDates?.marketVacancyLastUpdated);
  const currentQuarterFormatted = useMemo(() => format(new Date(), 'QQQ yyyy'), []);

  const lastData = chartData.length > 0 ? chartData[chartData.length - 1] : undefined;
  let vacantUnits = lastData ? lastData['Portfolio vacant units'] || lastData['QTD portfolio vacant units'] : 0;
  let occupiedUnits = lastData ? lastData['Portfolio occupied units'] || lastData['QTD portfolio occupied units'] : 0;

  if (hasMoodysData && buttonGroupFilter === VacantUnitsFilter.MARKET && lastData) {
    vacantUnits = lastData['Market vacant units'] || lastData['QTD market vacant units'];
    occupiedUnits = lastData['Market occupied units'] || lastData['QTD market occupied units'];
  }

  const title = getGraphTitle({
    graphName: 'Vacant units',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  return (
    <GraphWrapper
      graphId="vacant-units"
      title={title}
      subtitle={`Vacant units by portfolio${hasMoodysData ? ' and market' : ''}, based on the number of units advertised on Zillow Rentals Network.${lastUpdatedPortfolioDate && lastUpdatedMarketDate ? ` Portfolio data updated ${lastUpdatedPortfolioDate}, market data updated ${lastUpdatedMarketDate}.` : ''}`}
      buttonGroupFilter={hasMoodysData ? buttonGroupFilter : undefined}
      setButtonGroupFilter={hasMoodysData ? handleButtonGroupFilterChange : undefined}
      loading={isLoading || isFetchingInitialData}
      buttonGroupEnum={hasMoodysData ? VacantUnitsFilter : undefined}
      infoCards={[
        {
          title: 'Vacant units',
          infoText:
            buttonGroupFilter === VacantUnitsFilter.PORTFOLIO
              ? 'Estimated total vacant units for your portfolio or property in the selected market, this quarter.'
              : 'Estimated total vacant units for the selected market, this quarter.',
          value: vacantUnits,
          formatter: (value) => formatNumber(Number((value as number).toFixed(0))),
        },
        {
          title: 'Occupied units',
          infoText:
            buttonGroupFilter === VacantUnitsFilter.PORTFOLIO
              ? 'Estimated total occupied units for your portfolio or property in the selected market, this quarter.'
              : 'Estimated total occupied units for the selected market, this quarter.',
          value: occupiedUnits,
          formatter: (value) => formatNumber(Number((value as number).toFixed(0))),
        },
      ]}
    >
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={chartData}>
          <defs>
            <pattern
              id="stripe-portfolio-occupied"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#B88DFC" />
              <rect width="3" height="6" fill="#891EFA" />
            </pattern>
            <pattern
              id="stripe-portfolio-vacant"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#D0D0D8" />
              <rect width="3" height="6" fill="#ADADB6" />
            </pattern>
            <pattern
              id="stripe-market-occupied"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#73D4E4" />
              <rect width="3" height="6" fill="#009AAE" />
            </pattern>
            <pattern
              id="stripe-market-vacant"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#D0D0D8" />
              <rect width="3" height="6" fill="#ADADB6" />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" interval={0} tick={<QuarterCustomTick />} />
          <YAxis tickFormatter={(value) => formatStackedNumbers(value)} />
          <Tooltip
            content={
              <CustomGraphTooltip
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (filteredPayload: Array<any>, displayLabel: string) => {
                    const isCurrentQuarter = displayLabel === currentQuarterFormatted;
                    if (buttonGroupFilter === VacantUnitsFilter.PORTFOLIO) {
                      if (isCurrentQuarter) {
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) =>
                            entry.name === 'QTD portfolio occupied units' ||
                            entry.name === 'QTD portfolio vacant units',
                        );
                      }
                      return filteredPayload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) =>
                          entry.name === 'Portfolio occupied units' || entry.name === 'Portfolio vacant units',
                      );
                    } else {
                      if (isCurrentQuarter) {
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) =>
                            entry.name === 'QTD market occupied units' || entry.name === 'QTD market vacant units',
                        );
                      }
                      return filteredPayload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.name === 'Market occupied units' || entry.name === 'Market vacant units',
                      );
                    }
                  }
                }
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={
                  buttonGroupFilter === VacantUnitsFilter.PORTFOLIO
                    ? { 'Quarter to date': 'striped' }
                    : { 'Quarter to date': 'striped-teal' }
                }
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (payload: Array<any>) => {
                    const qtdKeys =
                      buttonGroupFilter === VacantUnitsFilter.PORTFOLIO
                        ? ['QTD portfolio occupied units', 'QTD portfolio vacant units']
                        : ['QTD market occupied units', 'QTD market vacant units'];
                    const filtered = payload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) => !qtdKeys.includes(entry.value),
                    );
                    const hasQTD = payload.some(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) => qtdKeys.includes(entry.value),
                    );
                    if (hasQTD) {
                      filtered.push({
                        value: 'Quarter to date',
                        type: 'rect',
                        color: buttonGroupFilter === VacantUnitsFilter.PORTFOLIO ? '#891EFA' : '#009AAE',
                      });
                    }
                    return filtered;
                  }
                }
              />
            }
          />
          {buttonGroupFilter === VacantUnitsFilter.PORTFOLIO && (
            <>
              <Bar dataKey="Portfolio occupied units" stackId="a" fill="#891EFA" shape={StackedBarShape} />
              <Bar dataKey="Portfolio vacant units" stackId="a" fill="#ADADB6" shape={StackedBarShape} />
              <Bar
                dataKey="QTD portfolio occupied units"
                stackId="a"
                fill="url(#stripe-portfolio-occupied)"
                shape={StackedBarShape}
              />
              <Bar
                dataKey="QTD portfolio vacant units"
                stackId="a"
                fill="url(#stripe-portfolio-vacant)"
                shape={StackedBarShape}
              />
            </>
          )}
          {hasMoodysData && buttonGroupFilter === VacantUnitsFilter.MARKET && (
            <>
              <Bar dataKey="Market occupied units" stackId="a" fill="#009AAE" shape={StackedBarShape} />
              <Bar dataKey="Market vacant units" stackId="a" fill="#ADADB6" shape={StackedBarShape} />
              <Bar
                dataKey="QTD market occupied units"
                stackId="a"
                fill="url(#stripe-market-occupied)"
                shape={StackedBarShape}
              />
              <Bar
                dataKey="QTD market vacant units"
                stackId="a"
                fill="url(#stripe-market-vacant)"
                shape={StackedBarShape}
              />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
